/**
 * What a game's own picture may be — the browser's half of the rule.
 *
 * Browser-safe on purpose: no storage SDK is imported here. `@dayon/storage`
 * restates the same types and ceiling in `game-art.ts`, and its test pins that
 * the two agree, so the picker cannot offer a file the store refuses.
 *
 * No SVG: it can carry a script, and these pictures are served from a public
 * address.
 */
export const GAME_PICTURE_TYPES = ['image/png', 'image/jpeg', 'image/webp'] as const;
export type GamePictureType = (typeof GAME_PICTURE_TYPES)[number];

/** 2 MB. */
export const MAX_GAME_PICTURE_BYTES = 2 * 1024 * 1024;

/** Advice, not a refusal: smaller pictures look soft on the game tile. */
export const MIN_GAME_PICTURE_PIXELS = 300;

/** The value for an input's accept attribute. */
export const GAME_PICTURE_ACCEPT = GAME_PICTURE_TYPES.join(',');

/** The field name a picture change carries in the configuration log and in a write's answer. */
export const GAME_PICTURE_FIELD = 'icon';

/**
 * Where a game's picture comes from, as `game.game.icon_source` records it. NULL
 * (not a member) means no operator has touched it; PROVIDER, an operator went
 * back to the provider's; UPLOADED, an upload is what players see.
 */
export const GAME_PICTURE_SOURCES = ['PROVIDER', 'UPLOADED'] as const;
export type GamePictureSource = (typeof GAME_PICTURE_SOURCES)[number];

/** Where a game's picture comes from, as the database records it. */
export interface GamePictureState {
  /** NULL: no operator has touched it. PROVIDER: an operator went back. UPLOADED: an upload shows. */
  source: GamePictureSource | null;
  key: string | null;
  url: string | null;
}

const UPLOADED_PREFIX = 'uploaded:';
const PROVIDER_PREFIX = 'provider:';

/**
 * One end of a picture change, as the configuration log stores it.
 *
 * The key or the address, not a bare kind word: replacing one upload with
 * another is a real change, and a log row whose from equals its to is a change
 * nobody made. An upload is named by its store key, because its address depends
 * on where the store is served from; the provider's picture by its address,
 * because that is all there is. NULL means no picture at all, and the tile draws
 * initials.
 */
export function gamePictureLogValue(state: GamePictureState): string | null {
  if (state.source === 'UPLOADED' && state.key) return UPLOADED_PREFIX + state.key;
  if (state.url) return PROVIDER_PREFIX + state.url;
  return null;
}

/**
 * The address a player's tile asks for.
 *
 * An uploaded picture is asked for with its upload time on the end, so
 * replacing it changes the address and a browser or cache holding the old one
 * fetches the new. A provider's address is passed through exactly as published:
 * it may be signed, and one more parameter could void the signature.
 */
export function versionedIconUrl(
  url: string | null,
  source: string | null,
  updatedAt: Date | string | null,
): string | null {
  if (url === null || source !== 'UPLOADED' || updatedAt === null) return url;
  const epoch = new Date(updatedAt).getTime();
  if (Number.isNaN(epoch)) return url;
  return url + (url.includes('?') ? '&' : '?') + 'v=' + String(epoch);
}

/**
 * Which kind of picture a stored log value names. UNKNOWN for anything this
 * module did not write, so a reader never guesses.
 */
export function gamePictureLogKind(
  value: string | null,
): 'UPLOADED' | 'PROVIDER' | 'NONE' | 'UNKNOWN' {
  if (value === null) return 'NONE';
  if (value.startsWith(UPLOADED_PREFIX)) return 'UPLOADED';
  if (value.startsWith(PROVIDER_PREFIX)) return 'PROVIDER';
  return 'UNKNOWN';
}
